<template>
  <div class="sa-card sa-card--no-scroll">
    <div class="et-header">
      <div>
        <div class="et-title">{{ metricLabel }} ao Longo do Tempo</div>
        <div class="et-subtitle">{{ metricLabel }} no período selecionado</div>
      </div>
      <div class="et-controls">
        <!-- Metric toggle -->
        <div class="et-metric-tabs">
          <button
            class="et-metric-tab"
            :class="{ active: metric === 'inscricoes' }"
            @click="metric = 'inscricoes'"
          >Inscrições</button>
          <button
            class="et-metric-tab et-metric-tab--green"
            :class="{ active: metric === 'confirmacoes' }"
            @click="metric = 'confirmacoes'"
          >Confirmações</button>
        </div>
        <!-- Chart type toggle -->
        <div class="et-type-toggle">
          <button
            class="et-type-btn"
            :class="{ active: chartType === 'area' }"
            @click="chartType = 'area'"
            title="Gráfico de linha"
          ><i class="bx bx-line-chart"></i></button>
          <button
            class="et-type-btn"
            :class="{ active: chartType === 'bar' }"
            @click="chartType = 'bar'"
            title="Gráfico de barras"
          ><i class="bx bx-bar-chart-alt-2"></i></button>
        </div>
        <!-- Granularity tabs -->
        <div class="et-gran-tabs">
          <button
            v-for="g in granularityOptions"
            :key="g.value"
            class="et-gran-tab"
            :class="{ active: granularity === g.value }"
            @click="setGranularity(g.value)"
          >{{ g.label }}</button>
        </div>
        <!-- Custom date range -->
        <div class="et-date-picker" v-click-outside="closeDatePicker">
          <button class="et-date-btn" @click="showDatePicker = !showDatePicker" :class="{ active: useCustomRange }">
            <i class="bx bx-calendar"></i>
          </button>
          <div class="et-date-dropdown" v-if="showDatePicker">
            <div class="et-date-field">
              <label>De</label>
              <input type="date" v-model="dateFrom" class="et-date-input" />
            </div>
            <div class="et-date-field">
              <label>Até</label>
              <input type="date" v-model="dateTo" class="et-date-input" />
            </div>
            <div class="et-date-actions">
              <button class="et-date-clear" @click="clearCustomRange">Limpar</button>
              <button class="et-date-apply" @click="applyCustomRange">Aplicar</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="et-loading">
      <b-spinner small variant="primary"></b-spinner>
    </div>

    <!-- Chart -->
    <template v-else-if="timelineData.length">
      <apexchart
        :key="chartType + granularity + metric + renderKey"
        :type="chartType"
        height="280"
        :options="chartOptions"
        :series="chartSeries"
      />
    </template>

    <!-- Empty -->
    <div v-else class="empty-state-card">
      <div class="empty-icon"><i class="bx bx-line-chart"></i></div>
      <div class="empty-title">Sem dados de timeline</div>
      <div class="empty-desc">Dados aparecerão com novas inscrições.</div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { getTimeline } from '../services'

export default {
  name: 'EnhancedTimeline',
  components: { apexchart: VueApexCharts },
  props: {
    period: { type: String, default: 'all' },
    parentDateFrom: { type: String, default: null },
    parentDateTo: { type: String, default: null }
  },
  data () {
    return {
      loading: false,
      timelineData: [],
      chartType: 'area',
      metric: 'inscricoes',
      granularity: 'day',
      granularityOptions: [
        { label: 'Dia', value: 'day' },
        { label: 'Semana', value: 'week' },
        { label: 'Mês', value: 'month' }
      ],
      showDatePicker: false,
      dateFrom: '',
      dateTo: '',
      useCustomRange: false,
      renderKey: 0
    }
  },
  computed: {
    metricLabel () {
      return this.metric === 'confirmacoes' ? 'Confirmações' : 'Inscrições'
    },
    metricColor () {
      return this.metric === 'confirmacoes' ? '#34c38f' : '#556ee6'
    },
    chartSeries () {
      const d = this.timelineData
      if (this.metric === 'confirmacoes') {
        return [{ name: 'Confirmações', data: d.map(t => t.confirmado || 0) }]
      }
      return [{ name: 'Inscrições', data: d.map(t => t.total || (t.confirmado + t.pendente + t.em_andamento + t.a_definir + t.declinado)) }]
    },
    chartOptions () {
      const labels = this.timelineData.map(t => t.label)
      const colors = [this.metricColor]

      const base = {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        colors,
        xaxis: {
          categories: labels,
          labels: {
            style: { fontSize: '10px', colors: '#8b90a5' },
            rotate: -45,
            rotateAlways: labels.length > 14
          }
        },
        yaxis: {
          labels: {
            style: { fontSize: '11px', colors: ['#8b90a5'] },
            formatter: (val) => Number.isInteger(val) ? val : ''
          }
        },
        grid: { borderColor: '#eaecf2', strokeDashArray: 4 },
        legend: { show: false },
        tooltip: { shared: true, intersect: false, theme: 'light' }
      }

      if (this.chartType === 'area') {
        return {
          ...base,
          chart: { ...base.chart, type: 'area' },
          stroke: { curve: 'smooth', width: 2.5 },
          fill: {
            type: 'gradient',
            gradient: { opacityFrom: 0.35, opacityTo: 0.05 }
          },
          dataLabels: { enabled: false }
        }
      }

      // bar
      return {
        ...base,
        chart: { ...base.chart, type: 'bar' },
        plotOptions: { bar: { columnWidth: '50%', borderRadius: 3 } },
        dataLabels: { enabled: false }
      }
    }
  },
  watch: {
    period () {
      if (!this.useCustomRange) this.loadData()
    },
    parentDateFrom () { if (!this.useCustomRange) this.loadData() },
    parentDateTo () { if (!this.useCustomRange) this.loadData() }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    async loadData () {
      this.loading = true
      try {
        const params = { granularity: this.granularity }
        if (this.useCustomRange && this.dateFrom && this.dateTo) {
          // Internal date picker takes priority
          params.date_from = this.dateFrom
          params.date_to = this.dateTo
        } else if (this.parentDateFrom && this.parentDateTo) {
          // Parent (dashboard) custom range
          params.date_from = this.parentDateFrom
          params.date_to = this.parentDateTo
        } else {
          params.period = this.period === 'custom' ? 'all' : this.period
        }
        const { data } = await getTimeline(params)
        this.timelineData = data.timeline || []
        this.renderKey++
      } catch (e) {
        console.error('Erro ao carregar timeline:', e)
      } finally {
        this.loading = false
      }
    },
    setGranularity (g) {
      this.granularity = g
      this.loadData()
    },
    applyCustomRange () {
      if (this.dateFrom && this.dateTo) {
        this.useCustomRange = true
        this.showDatePicker = false
        this.loadData()
      }
    },
    clearCustomRange () {
      this.dateFrom = ''
      this.dateTo = ''
      this.useCustomRange = false
      this.showDatePicker = false
      this.loadData()
    },
    closeDatePicker () {
      this.showDatePicker = false
    }
  },
  directives: {
    'click-outside': {
      bind (el, binding) {
        el._clickOutside = (e) => {
          if (!el.contains(e.target)) binding.value()
        }
        document.addEventListener('click', el._clickOutside)
      },
      unbind (el) {
        document.removeEventListener('click', el._clickOutside)
      }
    }
  }
}
</script>

<style scoped>
.et-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 10px;
}

.et-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a1d26;
}

.et-subtitle {
  font-size: 12px;
  color: #8b90a5;
  margin-top: 2px;
}

.et-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Metric toggle */
.et-metric-tabs {
  display: flex;
  background: #f5f6fa;
  border-radius: 6px;
  padding: 2px;
  gap: 2px;
}

.et-metric-tab {
  background: none;
  border: none;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #8b90a5;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.et-metric-tab:hover { color: #1a1d26; }

.et-metric-tab.active {
  background: #fff;
  color: #556ee6;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.et-metric-tab--green.active {
  color: #34c38f;
}

/* Type toggle */
.et-type-toggle {
  display: flex;
  background: #f5f6fa;
  border-radius: 6px;
  padding: 2px;
  gap: 2px;
}

.et-type-btn {
  background: none;
  border: none;
  padding: 5px 8px;
  border-radius: 4px;
  cursor: pointer;
  color: #8b90a5;
  font-size: 16px;
  line-height: 1;
  transition: all 0.15s ease;
}

.et-type-btn:hover { color: #1a1d26; }

.et-type-btn.active {
  background: #fff;
  color: #556ee6;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

/* Granularity tabs */
.et-gran-tabs {
  display: flex;
  background: #f5f6fa;
  border-radius: 6px;
  padding: 2px;
  gap: 2px;
}

.et-gran-tab {
  background: none;
  border: none;
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #8b90a5;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.et-gran-tab:hover { color: #1a1d26; }

.et-gran-tab.active {
  background: #fff;
  color: #556ee6;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

/* Date picker */
.et-date-picker { position: relative; }

.et-date-btn {
  background: #f5f6fa;
  border: none;
  padding: 5px 8px;
  border-radius: 6px;
  cursor: pointer;
  color: #8b90a5;
  font-size: 16px;
  line-height: 1;
  transition: all 0.15s ease;
}

.et-date-btn:hover { color: #1a1d26; }
.et-date-btn.active { color: #556ee6; background: rgba(85,110,230,0.1); }

.et-date-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  padding: 14px;
  box-shadow: 0 8px 24px rgba(26,29,38,0.1);
  z-index: 100;
  min-width: 220px;
}

.et-date-field {
  margin-bottom: 10px;
}

.et-date-field label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #8b90a5;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.et-date-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #eaecf2;
  border-radius: 6px;
  font-size: 13px;
  color: #1a1d26;
  outline: none;
  transition: border-color 0.15s ease;
}

.et-date-input:focus { border-color: #556ee6; }

.et-date-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 12px;
}

.et-date-clear,
.et-date-apply {
  padding: 5px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
}

.et-date-clear {
  background: #f5f6fa;
  color: #8b90a5;
}

.et-date-clear:hover { background: #eaecf2; color: #1a1d26; }

.et-date-apply {
  background: #556ee6;
  color: #fff;
}

.et-date-apply:hover { background: #4458c9; }

/* Loading */
.et-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 280px;
}

/* Responsive */
@media (max-width: 768px) {
  .et-header { flex-direction: column; align-items: flex-start; }
  .et-controls { flex-wrap: wrap; }
}

/* Empty state */
.empty-state-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  text-align: center;
}

.empty-state-card .empty-icon {
  width: 48px;
  height: 48px;
  background: #f5f6fa;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  color: #b0b5c8;
  font-size: 22px;
}

.empty-title {
  font-size: 13px;
  font-weight: 600;
  color: #4a5068;
  margin-bottom: 4px;
}

.empty-desc {
  font-size: 12px;
  color: #8b90a5;
  line-height: 1.5;
  max-width: 220px;
}
</style>
